/* Stable remote file slots. Local files/previews are never serialized into drafts. */
(() => {
  const controllers = new WeakMap();
  const mounts = new WeakMap();
  const baseDefaults = { files: null, previewFiles: null, urls: '', remotes: null, uploadStatus: 'idle', errors: null };
  let previewTail = Promise.resolve();
  const previewProfile = Object.freeze({
    maxDimension: typeof matchMedia === 'function' && matchMedia('(max-width: 767px)').matches ? 720 : 960,
    mimeType: 'image/jpeg', quality: 0.82
  });
  const maxSourceBytes = 50 * 1024 * 1024;
  const permanentErrors = Object.freeze({
    unsupported_image_type: 'unsupported_file',
    uploaded_file_signature_mismatch: 'corrupt_file',
    replacement_file_signature_mismatch: 'corrupt_file',
    uploaded_file_size_invalid: 'file_too_large',
    uploaded_file_size_mismatch: 'invalid_file',
    uploaded_file_type_mismatch: 'invalid_file',
    replacement_validation_failed: 'invalid_file'
  });
  function supportedImage(file) {
    const type = String(file?.type || '').toLowerCase();
    const name = String(file?.name || '').toLowerCase();
    return /^image\/(jpeg|png|webp|heic|heif)$/.test(type) || /\.(jpe?g|png|webp|heic|heif)$/.test(name);
  }

  function preview(file) {
    const job = previewTail.then(() => new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      const finish = blob => { URL.revokeObjectURL(url); img.src = ''; resolve(blob || null); };
      img.onerror = () => finish(null);
      img.onload = () => {
        const scale = Math.min(1, previewProfile.maxDimension / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        try {
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(blob => { canvas.width = canvas.height = 1; finish(blob); }, previewProfile.mimeType, previewProfile.quality);
        } catch (_) { finish(null); }
      };
      img.src = url;
    }));
    previewTail = job.catch(() => {});
    return job;
  }

  function create(state, options = {}) {
    const listeners = new Set();
    const locks = new Map();
    const reservations = new Map();
    const category = String(options.category || 'stamps');
    const arrayDefaults = { ...baseDefaults, ...(options.extraDefaults || {}) };
    let limit = Number(options.need || state.expected || 0);
    let resizing = false;
    const makePreview = options.preview || preview;
    const save = options.save || (() => window.tacDraftScheduleSave?.({ immediate: true, reason: category }));
    for (const key of Object.keys(arrayDefaults)) if (!Array.isArray(state[key])) state[key] = [];
    function ensure(n) {
      for (const [key, value] of Object.entries(arrayDefaults)) while (state[key].length < n) state[key].push(value);
      state.expected = limit = n;
    }
    ensure(limit);
    const emit = () => {
      listeners.forEach(fn => fn());
      if (typeof document !== 'undefined' && typeof document.dispatchEvent === 'function' && typeof CustomEvent === 'function') document.dispatchEvent(new CustomEvent('tac:file-slots', { detail: {
        category, state, need: limit, verified: verifiedCount(),
        statuses: Array.from(state.uploadStatus || []).slice(0, limit),
        errors: Array.from(state.errors || []).slice(0, limit)
      }}));
    };
    const occupied = i => !!(state.files[i] || state.remotes[i] || reservations.has(i) || locks.has(i));
    const verifiedCount = () => Array.from({ length: limit }, (_, i) => i).filter(i =>
      state.remotes[i]?.status === 'verified' && state.uploadStatus[i] === 'verified' && !locks.has(i)).length;
    const complete = () => limit > 0 && verifiedCount() === limit;
    const status = (i, value) => { state.uploadStatus[i] = value; emit(); };
    const revoke = url => { if (url?.startsWith('blob:')) URL.revokeObjectURL(url); };
    const reject = (i, code) => {
      state.files[i] = null;
      state.previewFiles[i] = null;
      state.errors[i] = code;
      status(i, 'failed');
      return false;
    };

    async function persist(i, file, hooks) {
      const session = await tacEnsureDraftSession();
      const records = await tacDraftListFiles(session);
      const actual = records.find(f => f.category === category && Number(f.position) === i + 1);
      const known = state.remotes[i];
      if (actual?.status === 'verified') {
        if (!known) {
          if (reservations.get(i) === actual.id) return tacDraftNormalizeRemoteFile(actual, file.name);
          throw new Error('restore_required');
        }
        if (actual.id !== known.id || actual.object_key !== known.object_key) throw new Error('slot_changed');
        return tacDraftNormalizeRemoteFile(await tacDraftReplaceFile(session, actual, file, hooks), file.name);
      }
      if (actual) {
        const ownsIncomplete = known ? actual.id === known.id : reservations.get(i) === actual.id;
        if (!ownsIncomplete) throw new Error('slot_changed');
        return tacDraftNormalizeRemoteFile(
          await tacDraftResumeReservedFile(session, actual, file, hooks),
          file.name
        );
      }
      return tacDraftNormalizeRemoteFile(await tacDraftUploadFreshFile(session, file, category, i + 1, hooks), file.name);
    }
    async function deleteRemote(i) {
      const session = await tacEnsureDraftSession();
      const records = await tacDraftListFiles(session);
      const actual = records.find(f => f.category === category && Number(f.position) === i + 1);
      if (!actual) return;
      const known = state.remotes[i];
      if (known ? (actual.id !== known.id || actual.object_key !== known.object_key) : reservations.get(i) !== actual.id) {
        throw new Error('slot_changed');
      }
      await tacDraftDeleteFile(session, actual.id);
    }

    function upload(i, file) {
      if (resizing || locks.has(i) || i < 0 || i >= limit) return Promise.resolve(false);
      if (!supportedImage(file)) return Promise.resolve(reject(i, 'unsupported_file'));
      if (file?.size != null && (!Number.isFinite(Number(file.size)) || Number(file.size) <= 0)) return Promise.resolve(reject(i, 'invalid_file'));
      if (file?.size != null && Number(file.size) > maxSourceBytes) return Promise.resolve(reject(i, 'file_too_large'));
      state.files[i] = file;
      state.errors[i] = null;
      status(i, 'queued');
      const job = Promise.resolve().then(async () => {
        let candidateURL = '';
        try {
          const light = await makePreview(file);
          candidateURL = light ? URL.createObjectURL(light) : '';
          if (!state.remotes[i] && candidateURL) {
            revoke(state.urls[i]); state.urls[i] = candidateURL; state.previewFiles[i] = light; candidateURL = '';
            emit();
          }
          const remote = await (options.persist || persist)(i, file, {
            onState: value => status(i, value),
            onReserved: value => { if (value?.id) reservations.set(i, value.id); }
          });
          if (remote?.status !== 'verified' || Number(remote.position) !== i + 1 || remote.category !== category) throw new Error('unverified');
          if (candidateURL) { revoke(state.urls[i]); state.urls[i] = candidateURL; candidateURL = ''; state.previewFiles[i] = light; }
          state.remotes[i] = remote;
          state.files[i] = null;
          reservations.delete(i);
          status(i, 'verified');
          save();
          return true;
        } catch (error) {
          revoke(candidateURL);
          const permanent = permanentErrors[String(error?.message || '')];
          if (permanent) {
            state.files[i] = null;
            state.previewFiles[i] = null;
            if (!state.remotes[i]) { revoke(state.urls[i]); state.urls[i] = ''; }
          }
          state.errors[i] = permanent || 'upload_failed';
          status(i, 'failed');
          return false;
        } finally { locks.delete(i); emit(); }
      });
      locks.set(i, job);
      return job;
    }

    function add(files) {
      const jobs = [];
      if (resizing) return Promise.resolve([]);
      for (const file of Array.from(files)) {
        const i = Array.from({ length: limit }, (_, index) => index).find(index => !occupied(index));
        if (i === undefined) break;
        jobs.push(upload(i, file));
      }
      return Promise.all(jobs);
    }
    function remove(i) {
      if (!Number.isInteger(i) || i < 0 || i >= state.files.length || locks.has(i)) return Promise.resolve(false);
      const job = Promise.resolve().then(async () => {
        try {
          if (state.remotes[i] || reservations.has(i)) await (options.deleteRemote || deleteRemote)(i);
          revoke(state.urls[i]);
          for (const [key, value] of Object.entries(arrayDefaults)) state[key][i] = value;
          reservations.delete(i);
          save();
          return true;
        } catch (_) { state.errors[i] = 'delete_failed'; return false; }
        finally { locks.delete(i); emit(); }
      });
      locks.set(i, job); emit();
      return job;
    }
    async function resize(n) {
      if (!Number.isInteger(n) || n < 1 || resizing || locks.size) return false;
      resizing = true;
      try {
        for (let i = state.files.length - 1; i >= n; i--) if (occupied(i) && !(await remove(i))) return false;
        for (const key of Object.keys(arrayDefaults)) { if (key === 'urls') state.urls.slice(n).forEach(revoke); state[key].length = Math.min(n, state[key].length); }
        ensure(n); emit(); return true;
      } finally { resizing = false; }
    }
    return { state, category, add, remove, resize, replace: upload, retry: i => state.uploadStatus[i] === 'failed' ? upload(i, state.files[i]) : Promise.resolve(false),
      complete, verifiedCount, busy: i => locks.has(i), pending: () => locks.size > 0 || resizing, need: () => limit,
      releasePreviews() { for (let i = 0; i < limit; i++) { revoke(state.urls[i]); state.urls[i] = ''; state.previewFiles[i] = null; } },
      subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
      idle: () => Promise.all(Array.from(locks.values())) };
  }

  function getGeneric(state, options) {
    if (!controllers.has(state)) controllers.set(state, create(state, options));
    return controllers.get(state);
  }
  function get(state, need) { return getGeneric(state, { need, category: 'stamps' }); }
  const copy = {
    es: { idle: 'Sin foto', queued: 'En espera', uploading: 'Subiendo…', verifying: 'Verificando…', verified: 'Guardada', failed: 'No se pudo subir', retry: 'Reintentar', remove: 'Quitar', replace: 'Cambiar', delete_failed: 'No se pudo quitar. Intenta de nuevo.', upload_failed: 'No se pudo subir. Intenta de nuevo.', unsupported_file: 'Formato no compatible', corrupt_file: 'La imagen está dañada', invalid_file: 'La imagen no es válida', file_too_large: 'La imagen supera 50 MB', reselect: 'Selecciona de nuevo esta foto', replacement_reselect: 'Se conserva la foto anterior. Selecciona otra vez para cambiarla.', count: '{got} / {need} guardadas' },
    en: { idle: 'No photo', queued: 'Queued', uploading: 'Uploading…', verifying: 'Verifying…', verified: 'Saved', failed: 'Upload failed', retry: 'Retry', remove: 'Remove', replace: 'Change', delete_failed: 'Could not remove. Try again.', upload_failed: 'Could not upload. Try again.', unsupported_file: 'Unsupported format', corrupt_file: 'The image is damaged', invalid_file: 'The image is not valid', file_too_large: 'The image exceeds 50 MB', reselect: 'Select this photo again', replacement_reselect: 'The previous photo is still saved. Select again to replace it.', count: '{got} / {need} saved' }
  };
  // Extend the existing dictionary; language selection remains owned by window.t.
  for (const [lang, entries] of Object.entries(copy)) {
    window.tacCopy ||= {};
    window.tacCopy[lang] ||= {};
    for (const [key, value] of Object.entries(entries)) window.tacCopy[lang]['stamps_slot_' + key] = value;
  }
  function mount(body, step, onValidity) {
    window.TACPreviews?.unmount(body);
    mounts.get(body)?.();
    const state = window.tacCustomizer.data.fotos_estampas ||= {};
    const engine = get(state, Number(step.cantidad));
    const t = window.t;
    const text = Object.fromEntries(Object.keys(copy.es).map(key => [key, t('stamps_slot_' + key)]));
    const root = document.createElement('div'); root.className = 'fe-wrap tac-stamps';
    const title = document.createElement('div'); title.className = 'fe-title'; title.textContent = t('fotos_estampas_title');
    const counter = document.createElement('div'); counter.className = 'fe-counter'; counter.setAttribute('aria-live', 'polite');
    const addButton = document.createElement('button'); addButton.type = 'button'; addButton.className = 'fe-btn fe-btn--primary'; addButton.textContent = t('btn_upload_photos');
    const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif'; input.multiple = true; input.hidden = true;
    const replacement = document.createElement('input'); replacement.type = 'file'; replacement.accept = input.accept; replacement.hidden = true;
    let replacementIndex = null;
    const grid = document.createElement('div'); grid.className = 'fe-grid';
    root.append(title, counter, addButton, grid, input, replacement); body.replaceChildren(root);
    const rows = Array.from({ length: engine.need() }, (_, i) => {
      const box = document.createElement('div'); box.className = 'fe-slot'; box.dataset.position = i + 1;
      const number = document.createElement('span'); number.className = 'fe-slot-index'; number.textContent = i + 1;
      const img = document.createElement('img'); img.alt = t('fotos_estampas_alt', { i: i + 1 }); img.loading = 'lazy'; img.decoding = 'async';
      const label = document.createElement('span'); label.className = 'tac-stamp-status';
      const actions = document.createElement('div'); actions.className = 'tac-stamp-actions';
      const buttons = {};
      for (const action of ['replace', 'retry', 'remove']) {
        const b = document.createElement('button'); b.type = 'button'; b.textContent = text[action]; b.setAttribute('aria-label', `${text[action]} ${i + 1}`);
        b.onclick = () => { if (action === 'replace') { replacementIndex = i; replacement.click(); } else engine[action](i); };
        buttons[action] = b; actions.append(b);
      }
      box.append(number, img, label, actions); grid.append(box);
      const privatePreview = window.TACPreviews?.mount(root, img, {
        remote: () => state.remotes[i], url: () => state.urls[i], setURL: url => { state.urls[i] = url; }, localBlob: () => state.previewFiles[i], releaseLocal: () => { state.previewFiles[i] = null; }
      });
      return { box, img, label, buttons, privatePreview };
    });
    addButton.onclick = () => input.click();
    input.onchange = () => { engine.add(input.files); input.value = ''; };
    replacement.onchange = () => { if (replacement.files[0]) engine.replace(replacementIndex, replacement.files[0]); replacement.value = ''; };
    let unsubscribe = () => {};
    function update() {
      if (!root.isConnected || !body.contains(root)) { unsubscribe(); return; }
      counter.textContent = text.count.replace('{got}', engine.verifiedCount()).replace('{need}', engine.need());
      rows.forEach(({ box, img, label, buttons, privatePreview }, i) => {
        const value = state.uploadStatus[i]; const has = !!(state.files[i] || state.remotes[i]); const busy = engine.busy(i);
        box.dataset.status = value;
        if (state.urls[i] && img.getAttribute('src') !== state.urls[i]) img.src = state.urls[i];
        if (!state.urls[i]) img.removeAttribute('src'); img.hidden = !state.urls[i];
        privatePreview?.refresh();
        label.textContent = window.TACPrepaidAccess?.blocked && state.files[i]
          ? (String(window.tacLang || 'es').startsWith('en') ? 'Continue from your account' : 'Continúa desde tu cuenta')
          : state.errors[i] ? text[state.errors[i]] : text[value];
        buttons.retry.hidden = value !== 'failed' || !state.files[i]; buttons.replace.hidden = !state.remotes[i]; buttons.remove.hidden = !has;
        for (const b of Object.values(buttons)) b.disabled = busy || !!window.TACPrepaidAccess?.blocked;
      });
      onValidity(engine.complete());
    }
    document.addEventListener('tac:prepaid-access', update);
    const stop = engine.subscribe(update);
    unsubscribe = () => { stop(); document.removeEventListener('tac:prepaid-access', update); };
    mounts.set(body, unsubscribe); update();
    if (typeof document !== 'undefined' && typeof document.dispatchEvent === 'function' && typeof CustomEvent === 'function') document.dispatchEvent(new CustomEvent('tac:file-slots', { detail: { category: 'stamps', state, need: engine.need(), verified: engine.verifiedCount(), statuses: Array.from(state.uploadStatus || []).slice(0, engine.need()), errors: Array.from(state.errors || []).slice(0, engine.need()) } }));
    return engine;
  }
  const complete = (state, need) => controllers.has(state) ? controllers.get(state).need() === need && controllers.get(state).complete() : !!state && need > 0 && Array.from({ length: need }, (_, i) => i).every(i => state.remotes?.[i]?.status === 'verified' && state.uploadStatus?.[i] === 'verified');
  const count = state => (state?.remotes || []).filter((remote, i) => remote?.status === 'verified' && state.uploadStatus?.[i] === 'verified').length;
  window.TACFileSlots = { create, get: getGeneric, complete, count, preview, previewProfile, supportedImage, pending: state => Boolean(controllers.get(state)?.pending()) };
  window.TACStamps = { create, get, mount, copy,
    complete, count };
})();
